import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { toPublicAgencyCard } from './utils/agencyUrl';

const FavoritesContext = createContext(null);
const STORAGE_KEY = 'favorites-public-v1';

function readFavorites() {
    try {
        const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
        if (!Array.isArray(saved)) return [];
        return saved.map(toPublicAgencyCard).filter(Boolean);
    } catch {
        return [];
    }
}

export function FavoritesProvider({ children }) {
    const [favorites, setFavorites] = useState(readFavorites);

    useEffect(() => {
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(favorites)); } catch { /* Storage may be unavailable. */ }
    }, [favorites]);

    useEffect(() => {
        const onStorage = event => {
            if (event.key === STORAGE_KEY) setFavorites(readFavorites());
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const isFavorite = useCallback(publicId => favorites.some(item => item.publicId === publicId), [favorites]);
    const toggleFavorite = useCallback(card => {
        const publicCard = toPublicAgencyCard(card);
        if (!publicCard) return;
        setFavorites(previous => previous.some(item => item.publicId === publicCard.publicId)
            ? previous.filter(item => item.publicId !== publicCard.publicId)
            : [...previous, publicCard]);
    }, []);
    const removeFavorite = useCallback(publicId => {
        setFavorites(previous => previous.filter(item => item.publicId !== publicId));
    }, []);
    const removeFavorites = useCallback(publicIds => {
        const removed = new Set(publicIds);
        if (!removed.size) return;
        setFavorites(previous => {
            const remaining = previous.filter(item => !removed.has(item.publicId));
            return remaining.length === previous.length ? previous : remaining;
        });
    }, []);
    const updateFavoriteDetails = useCallback(cards => {
        const publicCards = cards.map(toPublicAgencyCard).filter(Boolean);
        setFavorites(previous => previous.map(item => {
            const card = publicCards.find(candidate => candidate.publicId === item.publicId);
            return card && !item.name ? { ...card, ...item } : item;
        }));
    }, []);

    return <FavoritesContext.Provider value={{ favorites, isFavorite, toggleFavorite, removeFavorite, removeFavorites, updateFavoriteDetails }}>
        {children}
    </FavoritesContext.Provider>;
}

export function useFavorites() {
    const context = useContext(FavoritesContext);
    if (!context) throw new Error('useFavorites deve essere usato all’interno di FavoritesProvider');
    return context;
}
